import { API_AI, API_AI_USAGE } from './EsConstants';
import { esFetch, esPrimedCsrfToken, esRead } from './EsFetch';
import { useEffect, useState } from 'react';

/**
 * The browser's side of the shared AI service (es-ai-service.md §6). A STREAM task is a POST
 * answered as server-sent events -- `text` deltas, one `done` with the result, the usage, the
 * cents, and the outcome, or one `error` -- read with fetch and a ReadableStream rather than
 * EventSource, which can neither POST nor carry the XSRF header every write echoes. A CALL task
 * is a POST answered as JSON, and reads as esRead does: the body on 2xx, a throw carrying the
 * sentence otherwise.
 */

/** Everything esWrite sends, less the JSON Accept: the answer is an event stream. */
const headers = async ( pAccept ) => ( {
    'X-XSRF-TOKEN': await esPrimedCsrfToken(),
    'Accept': pAccept,
    'Content-Type': 'application/json',
} );

const refusal = ( pResponse, pText ) => {
    let aParsed = null;
    try {
        aParsed = pText ? JSON.parse( pText ) : null;
    }
    catch {
        // Not JSON: the login page, or nothing.
    }
    const aError = new Error( aParsed?.text || aParsed?.msg || aParsed?.message || ( 'ai answered ' + pResponse.status ) );
    aError.status = pResponse.status;
    aError.body = aParsed;
    return aError;
};

/**
 * One SSE frame: the lines up to a blank one. `event:` names it and the `data:` lines, joined by
 * newlines, are its JSON. A frame with no event name is a comment or a keep-alive and is dropped.
 */
export function esAiParseFrame( pFrame ) {
    let aEvent = null;
    const aData = [];
    for ( const aLine of pFrame.split( /\r?\n/ ) ) {
        if ( aLine.startsWith( 'event:' ) ) aEvent = aLine.slice( 6 ).trim();
        else if ( aLine.startsWith( 'data:' ) ) aData.push( aLine.slice( 5 ).replace( /^ /, '' ) );
    }
    if ( !aEvent ) return null;
    let aBody = null;
    try {
        aBody = aData.length ? JSON.parse( aData.join( '\n' ) ) : null;
    }
    catch {
        aBody = { text: aData.join( '\n' ) };
    }
    return { event: aEvent, data: aBody };
}

/**
 * Feed the stream's bytes through, frame by frame, to the three callbacks. Exported so a test
 * can drive it with a string rather than a network.
 */
export async function esAiReadEvents( pReader, { onText, onDone, onError } ) {
    const aDecoder = new TextDecoder();
    let aBuffer = '';
    let aEnded = false;
    const take = ( pFrame ) => {
        const aParsed = esAiParseFrame( pFrame );
        if ( !aParsed || aEnded ) return;
        if ( aParsed.event === 'text' ) onText?.( aParsed.data?.text ?? '' );
        else if ( aParsed.event === 'done' ) { aEnded = true; onDone?.( aParsed.data ); }
        else if ( aParsed.event === 'error' ) { aEnded = true; onError?.( aParsed.data?.msg ?? '' ); }
    };
    for ( ;; ) {
        const { value, done } = await pReader.read();
        if ( done ) break;
        aBuffer += aDecoder.decode( value, { stream: true } );
        let aBreak = aBuffer.search( /\r?\n\r?\n/ );
        while ( aBreak >= 0 ) {
            take( aBuffer.slice( 0, aBreak ) );
            aBuffer = aBuffer.slice( aBreak ).replace( /^\r?\n\r?\n/, '' );
            aBreak = aBuffer.search( /\r?\n\r?\n/ );
        }
    }
    if ( aBuffer.trim() ) take( aBuffer );
    if ( !aEnded ) onError?.( '' );
}

/**
 * Run a STREAM task. Resolves when the stream ends, after onDone or onError has been called; a
 * refusal before the stream starts (the flag, the consent, somebody else's campaign) is an
 * onError with esback's sentence, and the promise still resolves.
 */
export async function esAiStream( pTask, pBody, { onText, onDone, onError } = {} ) {
    let aResponse;
    try {
        aResponse = await esFetch( API_AI + '/' + pTask, {
            method: 'POST',
            headers: await headers( 'text/event-stream' ),
            body: JSON.stringify( pBody ?? {} ),
        } );
    }
    catch ( aError ) {
        onError?.( aError?.message ?? '' );
        return;
    }
    if ( !aResponse.ok || !aResponse.body ) {
        onError?.( refusal( aResponse, await aResponse.text() ).message );
        return;
    }
    await esAiReadEvents( aResponse.body.getReader(), { onText, onDone, onError } );
}

/** Run a CALL task: the answer on 2xx, a throw carrying esback's sentence otherwise. */
export async function esAiCall( pTask, pBody ) {
    const aResponse = await esFetch( API_AI + '/' + pTask, {
        method: 'POST',
        headers: await headers( 'application/json' ),
        body: JSON.stringify( pBody ?? {} ),
    } );
    const aText = await aResponse.text();
    if ( !aResponse.ok ) throw refusal( aResponse, aText );
    return aText ? JSON.parse( aText ) : null;
}

/**
 * The meter: { spentCents, potCents, resetDate, chargedTo, site }, or null while loading and on a
 * refusal. With a campaignId the caller is seated at, the pot is the table GM's and chargedTo
 * names them; an administrator's is the site's spend against the ceiling.
 */
export function useEsAiUsage( pCampaignId ) {
    const [ usage, setUsage ] = useState( null );
    useEffect( () => {
        let aLive = true;
        const aUrl = pCampaignId == null ? API_AI_USAGE : API_AI_USAGE + '?campaignId=' + encodeURIComponent( pCampaignId );
        esRead( aUrl ).then( ( pUsage ) => { if ( aLive ) setUsage( pUsage ); } ).catch( () => {} );
        return () => { aLive = false; };
    }, [ pCampaignId ] );
    return usage;
}
